import { ChangeDetectorRef, Directive, DoCheck, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { Select } from 'primeng/select';
import { Location } from '../../location/location.model';
import { LocationService } from '../../location/location.service';

interface LoadingLocationOption {
  id: number;
  name: string;
  locationLoading: true;
}

type LocationOption = Location | LoadingLocationOption;

/**
 * Turns a PrimeNG location select into a server-paged picker. Components keep
 * their existing form bindings; only this directive owns the option list.
 */
@Directive({
  selector: 'p-select[appLazyLocationPicker]',
  standalone: true
})
export class LazyLocationPickerDirective implements OnInit, OnChanges, DoCheck, OnDestroy {
  // A bare attribute is represented as an empty string by Angular templates;
  // a bound value carries the managed-company id.
  @Input('appLazyLocationPicker') managedById?: number | string | null;

  private readonly select = inject(Select);
  private readonly locationService = inject(LocationService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly pageSize = 25;
  private readonly subscriptions = new Subscription();
  private readonly pinnedLocations = new Map<number, Location>();
  private readonly loadingPages = new Set<string>();
  private readonly loadedPages = new Set<number>();
  private readonly pendingLocationIds = new Set<number>();

  private page: LocationOption[] = [];
  private searchText = '';
  private filterTimer?: ReturnType<typeof setTimeout>;
  private queryVersion = 0;
  private hasMore = true;
  private loading = false;
  private initialized = false;
  private lastModelKey = '';

  ngOnInit(): void {
    this.initialized = true;
    this.select.filter = true;
    this.select.lazy = true;
    this.select.virtualScroll = true;
    this.select.virtualScrollItemSize = 38;
    this.select.virtualScrollOptions = {
      ...(this.select.virtualScrollOptions ?? {}),
      showLoader: true,
      delay: 0
    };
    this.select.dataKey = this.select.dataKey || 'id';
    this.select.optionDisabled = this.select.optionDisabled || 'locationLoading';

    this.subscriptions.add(this.select.onLazyLoad.subscribe(event => this.onLazyLoad(event)));
    this.subscriptions.add(this.select.onFilter.subscribe(event => this.onFilter(event.filter)));
    this.subscriptions.add(this.select.onChange.subscribe(event => this.pinSelectedValue(event.value)));
    this.subscriptions.add(this.select.onShow.subscribe(() => {
      this.pinCurrentValue();
      this.loadPage(0);
    }));

    this.refreshOptions();
    this.loadPage(0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized && changes['managedById']) {
      this.resetQuery();
    }
  }

  ngDoCheck(): void {
    this.pinCurrentValue();
  }

  ngOnDestroy(): void {
    if (this.filterTimer) clearTimeout(this.filterTimer);
    this.subscriptions.unsubscribe();
  }

  refreshSelectedLocation(location: Location): void {
    if (!location?.id) return;
    this.pinnedLocations.set(location.id, location);
    this.refreshOptions();
  }

  private onLazyLoad(event: { first?: number; last?: number }): void {
    if (this.searchText) {
      const loadedOptionCount = this.page.filter(option => !this.isLoadingOption(option)).length;
      if ((event.last ?? 0) >= Math.max(0, loadedOptionCount - 1)) {
        this.loadNextSearchPage();
      }
      return;
    }

    const pinnedCount = this.pinnedLocations.size;
    const first = Math.max(0, (event.first ?? 0) - pinnedCount);
    const last = Math.max(first, (event.last ?? first + this.pageSize) - pinnedCount - 1);
    const firstPage = Math.floor(first / this.pageSize);
    const lastPage = Math.floor(last / this.pageSize);
    for (let page = firstPage; page <= lastPage; page++) {
      this.loadPage(page);
    }
  }

  private onFilter(value: string): void {
    if (this.filterTimer) clearTimeout(this.filterTimer);
    this.filterTimer = setTimeout(() => {
      const normalized = (value ?? '').trim();
      if (normalized === this.searchText) return;
      this.searchText = normalized;
      this.resetQuery();
    }, 300);
  }

  private resetQuery(): void {
    this.queryVersion++;
    this.hasMore = true;
    this.loadedPages.clear();
    this.loadingPages.clear();
    this.page = [];
    this.loading = false;
    this.refreshOptions();
    this.loadPage(0);
  }

  private loadNextSearchPage(): void {
    if (!this.hasMore) return;
    const nextPage = this.loadedPages.size === 0 ? 0 : Math.max(...this.loadedPages) + 1;
    this.loadPage(nextPage);
  }

  private loadPage(pageNumber: number): void {
    if (pageNumber < 0 || this.loadedPages.has(pageNumber) || (pageNumber > 0 && !this.hasMore)) return;

    const queryVersion = this.queryVersion;
    const requestKey = `${queryVersion}:${pageNumber}`;
    if (this.loadingPages.has(requestKey)) return;

    this.loadingPages.add(requestKey);
    this.loading = true;
    this.refreshOptions();
    this.locationService.getAllComboData(
      this.searchText,
      pageNumber,
      this.pageSize,
      'en-US',
      this.managedCompanyId()
    ).subscribe({
      next: response => {
        if (queryVersion !== this.queryVersion) return;

        const locations = response.items ?? [];
        const total = Math.max(0, Number(response.count ?? locations.length), locations.length);
        const nextPage = Array.from({ length: total }, (_, index) =>
          this.page[index] ?? this.loadingOption(index)
        );
        locations.forEach((location, index) => {
          const optionIndex = pageNumber * this.pageSize + index;
          if (optionIndex < nextPage.length) nextPage[optionIndex] = location;
        });
        this.page = nextPage;
        this.hasMore = response.hasMore;
        this.loadedPages.add(pageNumber);
        this.refreshOptions();
      },
      error: () => {
        if (queryVersion !== this.queryVersion) return;
        this.loadingPages.delete(requestKey);
        this.loading = this.loadingPages.size > 0;
        this.refreshOptions();
      },
      complete: () => {
        this.loadingPages.delete(requestKey);
        this.loading = this.loadingPages.size > 0;
        this.refreshOptions();
      }
    });
  }

  private pinCurrentValue(): void {
    const value = this.readModelValue();
    const key = this.modelKey(value);
    if (key === this.lastModelKey) return;
    this.lastModelKey = key;
    this.pinSelectedValue(value);
  }

  private readModelValue(): unknown {
    const modelValue = (this.select as any).modelValue;
    return typeof modelValue === 'function' ? modelValue() : (this.select as any).value;
  }

  private modelKey(value: unknown): string {
    const values = Array.isArray(value) ? value : [value];
    return values.map(item => typeof item === 'object' && item ? (item as Location).id : item).join(',');
  }

  private pinSelectedValue(value: unknown): void {
    const values = Array.isArray(value) ? value : [value];
    values.forEach(item => {
      if (item && typeof item === 'object' && (item as Location).id) {
        this.pinLocation(item as Location);
        return;
      }
      const locationId = Number(item);
      if (!Number.isInteger(locationId) || locationId <= 0 || this.pinnedLocations.has(locationId)
        || this.page.some(option => !this.isLoadingOption(option) && option.id === locationId)
        || this.pendingLocationIds.has(locationId)) return;

      this.pendingLocationIds.add(locationId);
      this.locationService.getLocationById(locationId).subscribe({
        next: response => this.pinLocation(response.data?.[0]),
        complete: () => this.pendingLocationIds.delete(locationId),
        error: () => this.pendingLocationIds.delete(locationId)
      });
    });
  }

  private pinLocation(location?: Location): void {
    if (!location?.id || this.pinnedLocations.has(location.id)) return;
    this.pinnedLocations.set(location.id, location);
    this.refreshOptions();
  }

  private refreshOptions(): void {
    const pinned = Array.from(this.pinnedLocations.values());
    const pinnedIds = new Set(pinned.map(location => location.id));
    this.select.options = [
      ...pinned,
      ...this.page.map((option, index) =>
        !this.isLoadingOption(option) && !pinnedIds.has(option.id) ? option : this.loadingOption(index)
      )
    ];
    this.select.loading = this.loading;
    this.changeDetector.markForCheck();
  }

  private isLoadingOption(option: LocationOption | null | undefined): option is LoadingLocationOption {
    return !!option && 'locationLoading' in option;
  }

  private loadingOption(index: number): LoadingLocationOption {
    return { id: -1 - index, name: 'Loading location…', locationLoading: true };
  }

  private managedCompanyId(): number | undefined {
    const id = Number(this.managedById);
    return Number.isInteger(id) && id > 0 ? id : undefined;
  }
}
